import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { getEnvelopeStatusMeta } from '@/lib/contractStatus';
import { fetchScopeTenantIds } from '@/lib/mergedScope';
import { readAllRowsAsync } from '@/lib/pagedRead';
import {
  aggregationReadsEveryRow,
  groupByRefusalReason,
  groupedCountUsesRpc,
  scopeFilterFor,
  type WidgetReadScope,
} from '@/lib/widgetScope';
import type { AssigneeRecord } from '@/lib/assigneeName';
import { stageNameCell, type StageCell } from '@/lib/projectStageColumn';
import type {
  DynamicWidgetConfig,
  QueryResult,
  DataSource,
  DateRangeConfig,
  DateRangePreset,
  ProjectFieldFilter,
  FilterOperator,
  PercentageAggregationResult,
  UserFilterValue,
  DynamicFilterValue,
  DynamicWidgetFilters,
  DerivedFilters,
} from '@/types/dashboard';

/**
 * Project data shape for drawer list items (DASH-24.1)
 */
export interface ProjectListItemData {
  id: string;
  /** SUB-5.14. Which account this row belongs to, so the panel can say. */
  tenant_id: string | null;
  /**
   * SUB-5.14. ⚠️ THREE STATES, NOT A NAME-OR-NULL. `stage_name` alone cannot
   * distinguish "your role may not open this stage" from "the stage could not be
   * read", and collapsing them is the whole of F-50. `projectStageColumn.ts`
   * owns the three answers and the copy for two of them.
   */
  stage_cell: StageCell;
  project_name: string;
  company_name: string | null;
  project_status: string | null;
  current_stage_id: number | null;
  stage_name: string | null;
  pipeline_id: number | null;
  pipeline_name: string | null;
  assigned_sales_rep_id: string | null;
  assigned_rep_name: string | null;
  created_at: string;
  updated_at: string;
  stage_changed_at: string | null;
  system_cost: number | null;
  estimated_system_size: number | null;
  project_city: string | null;
  project_state: string | null;
}

/**
 * Paginated result for project list drawer (DASH-24.1)
 */
export interface ProjectListResult {
  items: ProjectListItemData[];
  total: number;
  hasMore: boolean;
  offset: number;
  limit: number;
}
import { isDynamicFilter } from '@/types/dashboard';
import type { PostgrestFilterBuilder } from '@supabase/postgrest-js';

/**
 * SUB-5.14. The columns the slide panel renders, shared by both reads.
 *
 * ⚠️ `tenant_id` IS IN THE LIST because a cross-account panel has to say which
 * account a row is from: it replaces the Pipeline column, whose name cannot be
 * resolved across accounts (no `merged_scope_pipeline_names` exists).
 *
 * ⚠️ THE THREE EMBEDS ARE KEPT even though they return null for a sub-account
 * row. In single-account scope they are the whole answer and cost one request;
 * in merged scope they answer for the anchor's rows and the RPCs fill the rest.
 * Removing them would make every single-account panel do two extra round trips
 * to learn what it already knew.
 */
/** One row of `merged_scope_stage_names(bigint[])`. */
interface StageNameRow {
  stage_id: number;
  stage_name: string | null;
  name_withheld: boolean;
}

const DRAWER_COLUMNS = `
          id,
          tenant_id,
          project_name,
          company_name,
          project_status,
          current_stage_id,
          pipeline_id,
          assigned_sales_rep_id,
          created_at,
          updated_at,
          stage_changed_at,
          system_cost,
          estimated_system_size,
          project_city,
          project_state,
          stages:current_stage_id (
            name
          ),
          pipelines:pipeline_id (
            name
          ),
          assigned_rep:assigned_sales_rep_id (
            first_name,
            last_name
          )
      `;

/**
 * SUB-5.12 · NFR8. A read this scope cannot honestly serve.
 *
 * ⚠️ **A DISTINCT TYPE, NOT A PLAIN `Error`, AND THE DIFFERENCE IS WHAT THE USER
 * READS.** `executeQuery` wraps anything it catches in "Failed to fetch widget
 * data: …", which is right for a network fault and wrong for this: nothing
 * failed. The widget is answerable in one account and not across several, the
 * registry knows why, and the reason is written to be shown. A surface can tell
 * the two apart with `instanceof` and render the explanation instead of an
 * error.
 *
 * The alternative was returning empty data, which is the silent subset NFR8
 * exists to forbid: a widget showing zero is indistinguishable from a widget
 * showing nothing because it was not allowed to look.
 */
export class WidgetScopeRefusal extends Error {
  readonly isScopeRefusal = true;

  constructor(reason: string) {
    super(reason);
    this.name = 'WidgetScopeRefusal';
  }
}

/**
 * Is this error a refusal rather than a failure?
 *
 * ⚠️ **NOT `instanceof`, AND THE REASON IS REACT QUERY.** The error a component
 * receives from `useQuery` has crossed a serialization-free but identity-hostile
 * boundary: an error thrown in a retried query, or reconstructed by any layer in
 * between, can arrive as a plain `Error` carrying the message and the fields but
 * not the prototype. `instanceof` then answers false and the refusal renders as a
 * red failure with a Retry button, which is exactly the presentation this class
 * was created to avoid.
 *
 * ⚠️ **THIS WAS A REAL DEFECT, NOT A PRECAUTION.** SUB-5.12 shipped the class
 * and never wired a surface to it, so every refusal rendered as "Unable to Load
 * Widget" over a correct explanation. Allan hit it within the hour.
 */
export function isWidgetScopeRefusal(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { isScopeRefusal?: boolean }).isScopeRefusal === true
    || (error as { name?: string }).name === 'WidgetScopeRefusal';
}

/**
 * Builds a FRESH row query. See `buildRowQuery` in `buildAndExecuteQuery` for
 * why freshness is a requirement rather than a convention.
 */
type RowQueryFactory = () => Promise<{ query: PostgrestFilterBuilder<any, any, any> }>;

/**
 * Dynamic Widget Query Service (DASH-8.1)
 * Translates DynamicWidgetConfig into Supabase queries with proper filtering
 */
export class DynamicWidgetQueryService {
  /**
   * Resolve dynamic filter values (DASH-22.4)
   * Converts "Current User" dynamic filters to actual user IDs
   *
   * @param filters - Array of user filter values (can be UUIDs or dynamic filters)
   * @returns Array of resolved user UUIDs
   */
  private static async resolveDynamicUserFilters(filters: UserFilterValue[]): Promise<string[]> {
    if (!filters || filters.length === 0) {
      return [];
    }

    const resolvedUserIds: string[] = [];

    // Get current user ID once (cache for performance)
    let currentUserId: string | null = null;

    for (const filter of filters) {
      if (isDynamicFilter(filter)) {
        // Dynamic "Current User" filter
        if (filter.type === 'current_user') {
          // Lazy load current user ID only if needed
          if (currentUserId === null) {
            const { data: { user }, error } = await supabase.auth.getUser();
            if (error || !user) {
              console.warn('[DynamicFilter] Failed to resolve current user:', error);
              continue; // Skip this filter if auth fails
            }
            currentUserId = user.id;
          }
          resolvedUserIds.push(currentUserId);
        }
      } else {
        // Regular user UUID
        resolvedUserIds.push(filter);
      }
    }

    return resolvedUserIds;
  }

  /**
   * Execute complete query based on widget configuration
   */
  static async executeQuery(config: DynamicWidgetConfig): Promise<QueryResult> {
    try {
      const startTime = performance.now();

      // Build and execute the query
      const data = await this.buildAndExecuteQuery(config);

      // Calculate trend if requested
      let trend;
      if (config.visualization.showTrend && config.aggregation.type !== 'list') {
        trend = await this.calculateTrend(config, data);
      }

      const endTime = performance.now();
      const executionTime = endTime - startTime;

      // Slow query warning (DASH-8.7: Performance monitoring)
      if (executionTime > 5000) {
        console.warn('[SLOW QUERY] DynamicWidget:', Math.round(executionTime) + 'ms', {
          dataSource: config.dataSource,
          filters: config.filters,
          aggregation: config.aggregation.type,
        });
      }

      return {
        data,
        metadata: {
          count: Array.isArray(data) ? data.length : 1,
          executionTime,
        },
        trend,
      };
    } catch (error: any) {
      // SUB-5.12 · AC3. A refusal is an answer, not a failure. Rethrow it
      // unwrapped so the surface can render its reason rather than
      // "Failed to fetch widget data: …".
      if (isWidgetScopeRefusal(error)) throw error;

      console.error('[DynamicWidgetQueryService] Query failed:', error);
      console.error('[DynamicWidgetQueryService] Error details:', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
      });
      const msg = error?.message || error?.details || error?.hint || (typeof error === 'string' ? error : 'Unknown error');
      throw new Error(`Failed to fetch widget data: ${msg}`);
    }
  }

  /**
   * Build and execute the complete query
   */
  private static async buildAndExecuteQuery(config: DynamicWidgetConfig): Promise<any> {
    // SUB-5.12 · AC2, AC3, AC6. Resolved once, for the whole query.
    const scope = await this.resolveReadScope(config.dataSource);

    // AC3 · NFR8. A source whose policy cannot follow merged scope says so
    // rather than rendering the anchor's rows under an All Accounts heading.
    if (scope.kind === 'refused') {
      throw new WidgetScopeRefusal(scope.reason);
    }

    // AC8 · D35. A group-by nobody classified is refused rather than charted,
    // and only in merged scope: in one account every field means one thing.
    if (scope.kind === 'merged' && config.visualization.groupBy) {
      const refusal = groupByRefusalReason(config.visualization.groupBy);
      if (refusal) throw new WidgetScopeRefusal(refusal);
    }

    // Extract checklist filter info (needed for aggregation queries)
    const checklistFilters = this.extractChecklistFilters(config.filters);

    // For count aggregations without groupBy, we need a different query building approach
    // because we can't chain multiple .select() calls
    const isSimpleCount = config.aggregation.type === 'count' && !config.visualization.groupBy;

    // Separate checklist filters: not_exists uses subquery exclusion (PostgREST LEFT JOIN
    // filters don't affect parent row count), all others use INNER JOIN.
    const notExistsFilters: ProjectFieldFilter[] = [];
    const joinableChecklistFilters: ProjectFieldFilter[] = [];

    if (config.filters.projectFields) {
      config.filters.projectFields.forEach((filter) => {
        if (filter.source === 'checklist_item' && filter.checklistItemId) {
          if (filter.operator === 'not_exists') {
            notExistsFilters.push(filter);
          } else {
            joinableChecklistFilters.push(filter);
          }
        }
      });
    }

    // Handle not_exists via subquery exclusion: find project IDs that HAVE the value
    // set, then exclude them from the main query. This correctly handles both cases:
    // (a) checklist item row exists but value is null, and (b) row doesn't exist at all.
    //
    // SUB-5.12: the ids are gathered ONCE, before the row query is built, so that
    // rebuilding that query per page (below) cannot re-run these subqueries.
    const excludedProjectIds: string[] = [];
    for (const filter of notExistsFilters) {
      const itemId = typeof filter.checklistItemId === 'string'
        ? parseInt(filter.checklistItemId, 10) : filter.checklistItemId;
      if (!itemId || isNaN(itemId as number)) continue;

      // Find projects that DO have this value set.
      // SUB-5.12: scoped the same way as the main query, or the exclusion set is
      // computed over one account and rows from the others are never excluded.
      let subQuery = this.applyScope(
        supabase
          .from('projects')
          .select('id, pci:project_checklist_items!inner(template_item_id)')
          .is('deleted_at', null) as unknown as PostgrestFilterBuilder<any, any, any>,
        scope,
      ).eq('pci.template_item_id', itemId);

      // Check the appropriate value column based on data type
      if (filter.dataType === 'date') {
        subQuery = subQuery.not('pci.value_date', 'is', null);
      } else if (filter.dataType === 'boolean') {
        subQuery = subQuery.eq('pci.is_completed', true);
      } else {
        subQuery = subQuery.not('pci.value', 'is', null);
      }

      const { data: projectsWithValue } = await subQuery.limit(10000);
      if (projectsWithValue && projectsWithValue.length > 0) {
        excludedProjectIds.push(...projectsWithValue.map((p: any) => p.id));
      }
    }

    /**
     * SUB-5.12 · F-101. One query, built from scratch, every time it is asked
     * for.
     *
     * ⚠️ A BUILDER CANNOT BE REUSED ACROSS PAGES. `range()` overwrites but
     * `order()` appends, so paging over one builder produces
     * `order=id.asc,id.asc,…` and a URL that grows per request. `readAllRows`
     * therefore takes a factory, and this is it.
     */
    const buildRowQuery = async (
      forCount: boolean,
    ): Promise<{ query: PostgrestFilterBuilder<any, any, any> }> => {
      let q: PostgrestFilterBuilder<any, any, any> = this.buildBaseQuery(
        config.dataSource,
        scope,
        forCount,
      );

      if (joinableChecklistFilters.length > 0) {
        const joinParts = joinableChecklistFilters.map((filter) => {
          const alias = `pci_${filter.checklistItemId}`;
          return `${alias}:project_checklist_items!inner(*)`;
        });
        q = forCount
          ? (q as any).select(`*, ${joinParts.join(', ')}`, { count: 'exact', head: true })
          : (q as any).select(`*, ${joinParts.join(', ')}`);
      }

      if (excludedProjectIds.length > 0) {
        q = q.not('id', 'in', `(${excludedProjectIds.join(',')})`);
      }

      // Apply all filters (DASH-22.4: Now async to resolve dynamic filters).
      // Destructure { query } because applyFilters wraps its return — see its docstring.
      // ⚠️ WRAPPED, NOT RETURNED BARE. A builder is a thenable, so a bare
      // `Promise<builder>` resolves by EXECUTING the query. Same idiom and same
      // reason as `applyFilters` below.
      return this.applyFilters(q, config.filters, config.dataSource);
    };

    const { query } = await buildRowQuery(isSimpleCount);

    // Handle aggregation (pass checklist filters to preserve JOINs)
    const result = await this.applyAggregation(
      query,
      config,
      config.visualization.groupBy,
      checklistFilters,
      scope,
      () => buildRowQuery(false),
    );

    return result;
  }

  /**
   * SUB-5.12 · The accounts THIS read may cover, resolved once per query.
   *
   * ⚠️ ONE RESOLUTION PER QUERY, NOT ONE PER SITE. The class used to call
   * `getCurrentTenantId()` at seven places, and a scope that is re-resolved
   * halfway through a query can change between the count and the rows it
   * counted. `fetchScopeTenantIds` is the same one server call `TenantContext`
   * makes; there is no second spelling of the rule here.
   *
   * ⚠️ FAILURE NARROWS. `fetchScopeTenantIds` returns the empty array when
   * nothing could be resolved, and `scopeFilterFor` then falls back to the
   * account already held. A merged read assembled because a status call failed
   * is the one direction there is no recovering from.
   */
  private static async resolveReadScope(dataSource: DataSource): Promise<WidgetReadScope> {
    const [reach, fallback] = await Promise.all([
      fetchScopeTenantIds(),
      getCurrentTenantId(),
    ]);
    return scopeFilterFor(dataSource, reach, fallback);
  }

  /**
   * Narrow a query to the accounts in scope.
   *
   * ⚠️ `.eq()` FOR ONE ACCOUNT, `.in()` ONLY FOR MORE THAN ONE, AND THAT IS
   * FR45. Every one of the 236 existing widgets runs in single-account scope and
   * must keep the query path it has, so the single-account branch emits exactly
   * the filter it always did rather than a one-element `.in()` that happens to
   * mean the same thing.
   */
  private static applyScope(
    query: PostgrestFilterBuilder<any, any, any>,
    scope: WidgetReadScope,
  ): PostgrestFilterBuilder<any, any, any> {
    if (scope.kind === 'merged') return query.in('tenant_id', scope.tenantIds);
    if (scope.kind === 'single') return query.eq('tenant_id', scope.tenantId);
    return query;
  }

  /**
   * Build base query for data source with tenant isolation
   * Optimized to select only necessary columns (DASH-8.7)
   *
   * SUB-5.12: takes the resolved scope rather than one tenant id. In
   * single-account scope the emitted query is unchanged.
   *
   * @param isSimpleCount - If true, don't call .select() yet (for count aggregations)
   */
  private static buildBaseQuery(
    dataSource: DataSource,
    scope: WidgetReadScope,
    isSimpleCount: boolean = false
  ): PostgrestFilterBuilder<any, any, any> {
    let query;

    switch (dataSource) {
      case 'projects':
        if (isSimpleCount) {
          // For count queries, just set up the table with basic filters
          // Don't call .select() because we'll call it later with count options
          query = supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .is('deleted_at', null);
        } else {
          // Select only columns commonly used in widgets (avoids fetching large JSONB/text fields)
          query = supabase
            .from('projects')
            .select(`
              id,
              project_name,
              company_name,
              tenant_id,
              pipeline_id,
              current_stage_id,
              project_status,
              assigned_sales_rep_id,
              assigned_internal_pm_id,
              assigned_installer_pm_id,
              created_at,
              updated_at,
              stage_changed_at,
              estimated_system_size,
              system_cost,
              project_state,
              project_city,
              project_zip_code
            `)
            .is('deleted_at', null);
        }
        break;

      case 'appointments':
        if (isSimpleCount) {
          query = supabase
            .from('appointments')
            .select('*', { count: 'exact', head: true });
        } else {
          query = supabase
            .from('appointments')
            .select(`
              id,
              lead_id,
              tenant_id,
              date_time,
              duration,
              disposition,
              notes,
              created_at,
              updated_at
            `);
        }
        break;

      case 'tasks':
        if (isSimpleCount) {
          query = supabase
            .from('tasks')
            .select('*', { count: 'exact', head: true });
        } else {
          query = supabase
            .from('tasks')
            .select(`
              id,
              project_id,
              tenant_id,
              title,
              description,
              status,
              priority,
              assignee_id,
              due_date,
              created_at,
              updated_at,
              completed_at
            `);
        }
        break;

      case 'notifications':
        if (isSimpleCount) {
          query = supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true });
        } else {
          query = supabase
            .from('notifications')
            .select(`
              id,
              user_id,
              type,
              title,
              message,
              read,
              created_at
            `);
        }
        break;

      case 'contracts':
        // CB-7.5 (FR-69): count/group v2 contracts by envelope status. Scoped to v2 — legacy
        // (Google Doc) contracts have no envelope_status, so a status widget over them is
        // meaningless. RLS is the security boundary; the .eq('tenant_id') is explicit narrowing.
        if (isSimpleCount) {
          query = supabase
            .from('contracts')
            .select('*', { count: 'exact', head: true })
            .eq('contract_model', 'v2');
        } else {
          query = supabase
            .from('contracts')
            .select(`
              id,
              tenant_id,
              project_id,
              contract_model,
              envelope_status,
              created_at,
              updated_at
            `)
            .eq('contract_model', 'v2');
        }
        break;

      default:
        throw new Error(`Unknown data source: ${dataSource}`);
    }

    // SUB-5.12. ⚠️ `notifications` is deliberately absent from the scope
    // application: it never carried a client-side tenant filter, its policy
    // `notifications_select` already resolves `merged_scope_tenant_ids()`, and
    // adding one here would narrow a query FR45 says must not change.
    const scoped =
      dataSource === 'notifications'
        ? (query as PostgrestFilterBuilder<any, any, any>)
        : this.applyScope(query as PostgrestFilterBuilder<any, any, any>, scope);

    return scoped;
  }

  /**
   * Apply all filters to the query.
   *
   * IMPORTANT: returns the builder wrapped in a plain object instead of returning
   * the builder directly. PostgrestFilterBuilder is itself a thenable, so an async
   * function that returns it would have its returned promise "follow" the builder
   * (per Promise/A+ resolution rules). The result: `await applyFilters(query)`
   * would execute the query and resolve with a PostgrestSingleResponse instead of
   * the builder, breaking any subsequent .order() / .range() chaining.
   * Wrapping in `{ query }` is a plain object — `await` returns it as-is and the
   * caller can keep chaining.
   */
  private static async applyFilters(
    query: PostgrestFilterBuilder<any, any, any>,
    filters: DynamicWidgetConfig['filters'],
    dataSource: DataSource
  ): Promise<{ query: PostgrestFilterBuilder<any, any, any> }> {
    // Pipeline filter
    if (filters.pipelineIds && filters.pipelineIds.length > 0) {
      query = query.in('pipeline_id', filters.pipelineIds);
    }

    // Stage filter (include selected stages)
    if (filters.stageIds && filters.stageIds.length > 0) {
      if (dataSource === 'projects') {
        query = query.in('current_stage_id', filters.stageIds);
      }
    }

    // User filter (DASH-22.4: Resolve dynamic filters)
    if (filters.assignedUserIds && filters.assignedUserIds.length > 0) {
      // Resolve dynamic "Current User" filters to actual user IDs
      const resolvedUserIds = await this.resolveDynamicUserFilters(filters.assignedUserIds);

      if (resolvedUserIds.length > 0) {
        if (dataSource === 'projects') {
          query = query.in('assigned_sales_rep_id', resolvedUserIds);
        } else if (dataSource === 'tasks') {
          query = query.in('assignee_id', resolvedUserIds);
        } else if (dataSource === 'appointments') {
          query = query.in('rep_id', resolvedUserIds);
        }
      }
    }

    // Status filter
    if (filters.statuses && filters.statuses.length > 0) {
      query = query.in('project_status', filters.statuses);
    }

    // Disposition filter (appointments only)
    if (filters.dispositions && filters.dispositions.length > 0 && dataSource === 'appointments') {
      query = query.in('disposition', filters.dispositions);
    }

    // Date range filter
    if (filters.dateRange) {
      query = this.applyDateRangeFilter(query, filters.dateRange, dataSource);
    }

    // Location filters (projects only)
    if (filters.location && dataSource === 'projects') {
      if (filters.location.states && filters.location.states.length > 0) {
        query = query.in('project_state', filters.location.states);
      }
      if (filters.location.cities && filters.location.cities.length > 0) {
        query = query.in('project_city', filters.location.cities);
      }
      if (filters.location.zipCodes && filters.location.zipCodes.length > 0) {
        query = query.in('project_zip_code', filters.location.zipCodes);
      }
    }

    // Numeric range filters (projects only)
    if (filters.ranges && dataSource === 'projects') {
      if (filters.ranges.systemSizeMin !== undefined) {
        query = query.gte('estimated_system_size', filters.ranges.systemSizeMin);
      }
      if (filters.ranges.systemSizeMax !== undefined) {
        query = query.lte('estimated_system_size', filters.ranges.systemSizeMax);
      }
      if (filters.ranges.systemCostMin !== undefined) {
        query = query.gte('system_cost', filters.ranges.systemCostMin);
      }
      if (filters.ranges.systemCostMax !== undefined) {
        query = query.lte('system_cost', filters.ranges.systemCostMax);
      }
    }

    // Activity threshold filter (DASH-11.1)
    // Show items with no activity for specified number of days
    if (filters.lastActivityThreshold) {
      const { days, dateField } = filters.lastActivityThreshold;

      // Calculate threshold date (X days ago)
      const now = new Date();
      const thresholdDate = new Date(now);
      thresholdDate.setDate(now.getDate() - days);
      thresholdDate.setHours(0, 0, 0, 0); // Start of day

      // Filter: dateField < thresholdDate (items older than threshold)
      query = query.lt(dateField, thresholdDate.toISOString());
    }

    // Project field filters (DASH-12.1)
    if (filters.projectFields && filters.projectFields.length > 0) {
      filters.projectFields.forEach((filter) => {
        query = this.applyProjectFieldFilter(query, filter);
      });
    }

    // Pending contracts filter (CONTRACTS-1.11)
    // Filter projects that have contracts awaiting signature
    if (filters.hasPendingContracts === true && dataSource === 'projects') {
      const pendingStatuses = [
        'Sent to Customer',
        'Pending Signature 1',
        'Pending Signature 2',
        'Pending Signature 3',
      ];

      // Get project IDs that have pending contracts
      const { data: projectsWithPendingContracts, error: contractError } = await supabase
        .from('contracts')
        .select('project_id')
        .in('status', pendingStatuses)
        .not('project_id', 'is', null);

      if (!contractError && projectsWithPendingContracts) {
        // Extract unique project IDs
        const projectIds = [...new Set(
          projectsWithPendingContracts
            .map((c) => c.project_id)
            .filter((id): id is string => id !== null)
        )];

        if (projectIds.length === 0) {
          // No projects have pending contracts - add impossible condition
          query = query.eq('id', '00000000-0000-0000-0000-000000000000');
        } else {
          // Filter to only projects with pending contracts
          query = query.in('id', projectIds);
        }
      } else if (contractError) {
        console.error('[PendingContractsFilter] Error fetching pending contracts:', contractError);
      }
    }

    // Wrap in a plain object so callers can `await` without the builder's
    // thenable being unwrapped — see the docstring above for the full reason.
    return { query };
  }

  /**
   * Apply a project field filter to the Supabase query (DASH-12.1, DASH-12.2)
   * Supports both standard project table fields and checklist item fields
   */
  private static applyProjectFieldFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    filter: ProjectFieldFilter
  ): PostgrestFilterBuilder<any, any, any> {
    const { source = 'project_table' } = filter;

    // DEBUG: Log filter routing
    console.log('[ProjectFieldFilter] Routing filter:', {
      field: filter.field,
      source,
      originalSource: filter.source,
      isChecklistItem: source === 'checklist_item'
    });

    // DASH-12.2: Route to appropriate filtering logic based on source
    if (source === 'project_table') {
      return this.applyStandardFieldFilter(query, filter);
    } else if (source === 'checklist_item') {
      return this.applyChecklistItemFilter(query, filter);
    }

    return query;
  }

  /**
   * Apply standard project table field filter (DASH-12.1)
   * Supports text, number, date, and boolean field types with appropriate operators
   */
  private static applyStandardFieldFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    filter: ProjectFieldFilter
  ): PostgrestFilterBuilder<any, any, any> {
    const { field, operator, value, dataType } = filter;

    switch (dataType) {
      case 'text':
        return this.applyTextFilter(query, field, operator, value);
      case 'number':
        return this.applyNumberFilter(query, field, operator, value);
      case 'date':
        return this.applyDateFilter(query, field, operator, value);
      case 'boolean':
        return this.applyBooleanFilter(query, field, operator);
      default:
        console.warn(`[ProjectFieldFilter] Unknown dataType: ${dataType}`);
        return query;
    }
  }

  /**
   * Apply text-based filter operators
   */
  private static applyTextFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    field: string,
    operator: FilterOperator,
    value: any
  ): PostgrestFilterBuilder<any, any, any> {
    // Sanitize value to prevent injection
    const sanitizedValue = String(value).trim();

    switch (operator) {
      case 'equals':
        return query.eq(field, sanitizedValue);
      case 'contains':
        return query.ilike(field, `%${sanitizedValue}%`);
      case 'starts_with':
        return query.ilike(field, `${sanitizedValue}%`);
      case 'ends_with':
        return query.ilike(field, `%${sanitizedValue}`);
      default:
        return query;
    }
  }

  /**
   * Apply number-based filter operators
   */
  private static applyNumberFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    field: string,
    operator: FilterOperator,
    value: any
  ): PostgrestFilterBuilder<any, any, any> {
    switch (operator) {
      case 'equals':
        return query.eq(field, value);
      case 'greater_than':
        return query.gt(field, value);
      case 'less_than':
        return query.lt(field, value);
      case 'between':
        // value should be { min: number, max: number }
        if (value && typeof value === 'object' && 'min' in value && 'max' in value) {
          return query.gte(field, value.min).lte(field, value.max);
        }
        return query;
      default:
        return query;
    }
  }

  /**
   * Apply date-based filter operators
   */
  private static applyDateFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    field: string,
    operator: FilterOperator,
    value: any
  ): PostgrestFilterBuilder<any, any, any> {
    switch (operator) {
      case 'equals':
        // For dates, equals means same day
        const startOfDay = new Date(value);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(value);
        endOfDay.setHours(23, 59, 59, 999);
        return query
          .gte(field, startOfDay.toISOString())
          .lte(field, endOfDay.toISOString());
      case 'before':
        return query.lt(field, new Date(value).toISOString());
      case 'after':
        return query.gt(field, new Date(value).toISOString());
      case 'between':
        // value should be { start: Date, end: Date }
        if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
          return query
            .gte(field, new Date(value.start).toISOString())
            .lte(field, new Date(value.end).toISOString());
        }
        return query;
      default:
        return query;
    }
  }

  /**
   * Apply boolean-based filter operators
   */
  private static applyBooleanFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    field: string,
    operator: FilterOperator
  ): PostgrestFilterBuilder<any, any, any> {
    switch (operator) {
      case 'is_true':
        return query.eq(field, true);
      case 'is_false':
        return query.eq(field, false);
      default:
        return query;
    }
  }

  /**
   * Apply checklist item filter using JOIN operations (DASH-12.2)
   *
   * IMPORTANT: project_checklist_items schema:
   * - value: jsonb (contains the actual value)
   * - value_date: date (for DATE_INPUT types)
   * - is_completed: boolean (for CHECKBOX types)
   * - assigned_user_id: uuid (user assigned to this item)
   * - template_item_id: bigint (reference to checklist_template_items)
   *
   * SECURITY: Validates checklistItemId before building query
   */
  private static applyChecklistItemFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    filter: ProjectFieldFilter
  ): PostgrestFilterBuilder<any, any, any> {
    const { checklistItemId, operator } = filter;

    // DEBUG: Log filter being applied
    console.log('[ChecklistItemFilter] Applying filter:', {
      checklistItemId,
      checklistItemIdType: typeof checklistItemId,
      operator,
      fullFilter: filter
    });

    // SECURITY: Validate checklistItemId is present
    if (!checklistItemId) {
      console.warn('[ChecklistItemFilter] Missing checklistItemId');
      return query;
    }

    // Parse checklistItemId to number (it's stored as string in filter but DB expects number)
    const itemId = typeof checklistItemId === 'string' ? parseInt(checklistItemId, 10) : checklistItemId;
    if (isNaN(itemId)) {
      console.warn('[ChecklistItemFilter] Invalid checklistItemId:', checklistItemId);
      return query;
    }

    console.log('[ChecklistItemFilter] Parsed itemId:', itemId, 'for operator:', operator);

    // Build JOIN alias (unique per filter to allow multiple checklist filters)
    // NOTE: The JOIN is already added to the select clause in buildAndExecuteQuery.
    // Here we only apply filter conditions — do NOT call .select() again.
    const joinAlias = `pci_${itemId}`;

    switch (operator) {
      case 'exists':
        // INNER JOIN already in select; filter by template_item_id + value IS set.
        // Checklist items are pre-created with null values when projects enter a pipeline,
        // so "exists" means a value has actually been entered, not just that a row exists.
        query = query.eq(`${joinAlias}.template_item_id`, itemId);
        if (filter.dataType === 'date') {
          return query.not(`${joinAlias}.value_date`, 'is', null);
        }
        if (filter.dataType === 'boolean') {
          return query.eq(`${joinAlias}.is_completed`, true);
        }
        return query.not(`${joinAlias}.value`, 'is', null);

      case 'not_exists':
        // Handled via subquery exclusion in buildAndExecuteQuery — skip here.
        // PostgREST LEFT JOIN filters don't affect parent row count, and INNER JOIN
        // misses projects without the checklist item row. The subquery approach
        // correctly handles both cases.
        return query;

      case 'is_completed':
        // For CHECKBOX items: is_completed = true
        return query
          .eq(`${joinAlias}.template_item_id`, itemId)
          .eq(`${joinAlias}.is_completed`, true);

      case 'not_completed':
        // For CHECKBOX items: is_completed = false OR NULL
        return query
          .eq(`${joinAlias}.template_item_id`, itemId)
          .or(`is_completed.eq.false,is_completed.is.null`, { foreignTable: joinAlias });

      default:
        // Value-based operators (equals, greater_than, etc.)
        return this.applyChecklistValueFilter(query, filter, joinAlias, itemId);
    }
  }

  /**
   * Apply value-based filters to checklist items (DASH-12.2)
   * Handles text, number, and date comparisons
   *
   * ACTUAL SCHEMA:
   * - value: JSONB column (e.g., {"value": "text"} for text/number)
   * - value_date: date column (for DATE_INPUT items)
   * - is_completed: boolean column (for CHECKBOX items)
   */
  private static applyChecklistValueFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    filter: ProjectFieldFilter,
    joinAlias: string,
    itemId: number
  ): PostgrestFilterBuilder<any, any, any> {
    const { operator, value, dataType } = filter;

    // INNER JOIN already added in buildAndExecuteQuery; just filter by template_item_id
    query = query.eq(`${joinAlias}.template_item_id`, itemId);

    // Apply operator based on data type
    switch (dataType) {
      case 'text':
        // Text values stored in JSONB: {"value": "text"}
        // Use ->> operator to extract as text
        return this.applyTextFilterJSONB(query, `${joinAlias}.value`, operator, value);

      case 'number':
        // Number values stored in JSONB: {"value": "123"}
        // Extract and cast to numeric
        return this.applyNumberFilterJSONB(query, `${joinAlias}.value`, operator, value);

      case 'date':
        // Date values stored in separate value_date column
        return this.applyDateFilter(query, `${joinAlias}.value_date`, operator, value);

      case 'boolean':
        // Already handled by is_completed/not_completed operators
        return query;

      default:
        return query;
    }
  }

  /**
   * Apply text filter on JSONB value column
   * Extracts text from {"value": "text"} structure
   */
  private static applyTextFilterJSONB(
    query: PostgrestFilterBuilder<any, any, any>,
    jsonbColumn: string,
    operator: FilterOperator,
    value: any
  ): PostgrestFilterBuilder<any, any, any> {
    // Extract text value from JSONB: value->>'value'
    const textPath = `${jsonbColumn}->value`;

    switch (operator) {
      case 'equals':
        return query.eq(textPath, value);
      case 'contains':
        return query.ilike(textPath, `%${value}%`);
      case 'starts_with':
        return query.ilike(textPath, `${value}%`);
      case 'ends_with':
        return query.ilike(textPath, `%${value}`);
      default:
        return query;
    }
  }

  /**
   * Apply number filter on JSONB value column
   * Extracts number from {"value": "123"} structure and casts to numeric
   */
  private static applyNumberFilterJSONB(
    query: PostgrestFilterBuilder<any, any, any>,
    jsonbColumn: string,
    operator: FilterOperator,
    value: any
  ): PostgrestFilterBuilder<any, any, any> {
    // Extract numeric value from JSONB: (value->>'value')::numeric
    // Note: Supabase PostgREST doesn't support direct casting in filters
    // We'll use the text value and let PostgreSQL handle implicit casting
    const textPath = `${jsonbColumn}->value`;

    switch (operator) {
      case 'equals':
        return query.eq(textPath, value.toString());
      case 'greater_than':
        // Use gt with text, PostgreSQL will cast for numeric comparison
        return query.gt(textPath, value.toString());
      case 'less_than':
        return query.lt(textPath, value.toString());
      case 'between':
        if (value && typeof value === 'object' && 'min' in value && 'max' in value) {
          return query
            .gte(textPath, value.min.toString())
            .lte(textPath, value.max.toString());
        }
        return query;
      default:
        return query;
    }
  }

  /**
   * Apply date range filter based on preset or custom dates
   */
  private static applyDateRangeFilter(
    query: PostgrestFilterBuilder<any, any, any>,
    dateRange: DateRangeConfig,
    dataSource: DataSource
  ): PostgrestFilterBuilder<any, any, any> {
    // All Time means no date filtering (handle both "all_time" and "all time")
    const normalizedType = dateRange.type?.replace(/\s+/g, '_');
    if (normalizedType === 'all_time') return query;

    const dateField = dateRange.dateField || 'created_at';
    const { start, end } = this.calculateDateRange(dateRange.type, dateRange.customStart, dateRange.customEnd);

    query = query.gte(dateField, start.toISOString());
    if (end) {
      query = query.lt(dateField, end.toISOString());
    }

    return query;
  }

  /**
   * Calculate date boundaries for date range preset
   */
  private static calculateDateRange(
    preset: DateRangePreset,
    customStart?: string,
    customEnd?: string
  ): { start: Date; end: Date | null } {
    const now = new Date();
    let start: Date;
    let end: Date | null = null;

    // Normalize preset — handle "all time" (with space) and "all_time"
    const normalizedPreset = preset?.replace(/\s+/g, '_') as DateRangePreset;

    switch (normalizedPreset) {
      case 'all_time':
        // All time = no date filtering — return widest possible range
        start = new Date(2000, 0, 1);
        end = new Date(now.getFullYear() + 1, 0, 1);
        break;

      case 'yesterday':
        start = new Date(now);
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 1);
        break;

      case 'today':
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 1);
        break;

      case 'this_week':
        start = new Date(now);
        start.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 7);
        break;

      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        break;

      case 'this_quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), (quarter + 1) * 3, 1);
        break;

      case 'this_year':
        start = new Date(now.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear() + 1, 0, 1);
        break;

      case 'last_7_days':
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;

      case 'last_30_days':
        start = new Date(now);
        start.setDate(now.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;

      case 'custom':
        if (!customStart) {
          throw new Error('Custom date range requires customStart');
        }
        start = new Date(customStart);
        end = customEnd ? new Date(customEnd) : null;
        break;

      default:
        // Graceful fallback for unknown presets — treat as all time
        console.warn(`Unknown date range preset: ${preset}, falling back to all time`);
        start = new Date(2000, 0, 1);
        end = new Date(now.getFullYear() + 1, 0, 1);
        break;
    }

    return { start, end };
  }

  /**
   * Extract checklist item filter information for JOIN preservation
   */
  private static extractChecklistFilters(filters: DynamicWidgetConfig['filters']): Map<string, string> {
    const checklistJoins = new Map<string, string>();

    console.log('[extractChecklistFilters] Input filters.projectFields:', filters.projectFields);

    if (filters.projectFields && filters.projectFields.length > 0) {
      filters.projectFields.forEach((filter) => {
        console.log('[extractChecklistFilters] Checking filter:', {
          field: filter.field,
          source: filter.source,
          checklistItemId: filter.checklistItemId,
          isChecklistFilter: filter.source === 'checklist_item' && !!filter.checklistItemId
        });
        // not_exists is handled via subquery exclusion (no JOIN), so skip it here
        if (filter.source === 'checklist_item' && filter.checklistItemId && filter.operator !== 'not_exists') {
          const joinAlias = `pci_${filter.checklistItemId}`;
          checklistJoins.set(filter.checklistItemId, joinAlias);
          console.log('[extractChecklistFilters] Added JOIN:', joinAlias);
        }
      });
    }

    console.log('[extractChecklistFilters] Result:', checklistJoins.size, 'JOINs');
    return checklistJoins;
  }

  /**
   * Build SELECT clause with checklist JOINs included
   */
  private static buildSelectWithChecklistJoins(
    fields: string | string[],
    checklistFilters: Map<string, string>
  ): string {
    const fieldList = Array.isArray(fields) ? fields : [fields];
    const selectParts = [...fieldList];

    // Add checklist item JOINs
    checklistFilters.forEach((alias, itemId) => {
      selectParts.push(`${alias}:project_checklist_items!inner(*)`);
    });

    return selectParts.join(', ');
  }

  /**
   * Apply aggregation to query and return results
   */
  private static async applyAggregation(
    query: PostgrestFilterBuilder<any, any, any>,
    config: DynamicWidgetConfig,
    groupBy: string | undefined,
    checklistFilters: Map<string, string>,
    scope: WidgetReadScope,
    makeRowQuery: RowQueryFactory,
  ): Promise<any> {
    const aggregation = config.aggregation;

    switch (aggregation.type) {
      case 'count':
        if (groupBy) {
          // Count with groupBy returns grouped results
          return await this.executeGroupByCount(groupBy, config, checklistFilters, scope, makeRowQuery);
        } else {
          // Simple count - the .select() with count options was already called in buildBaseQuery
          // So we just need to execute the query
          const { count, error } = await query;
          if (error) throw error;
          return count ?? 0;
        }

      case 'sum':
      case 'avg':
      case 'min':
      case 'max':
        if (!aggregation.field) {
          throw new Error(`${aggregation.type} aggregation requires a field`);
        }
        return await this.executeNumericAggregation(aggregation.type, aggregation.field, groupBy, makeRowQuery);

      case 'list':
        // Progressive loading: Use .range() for efficient pagination (DASH-8.7)
        const limit = aggregation.limit || 20; // Increased default to 20
        const offset = aggregation.offset || 0; // Support pagination offset

        // Use .range() instead of .limit() for better performance
        const { data, error, count } = await query
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);

        if (error) throw error;

        // Return data with pagination metadata
        return {
          items: data || [],
          total: count,
          hasMore: count ? (offset + limit) < count : false,
          offset,
          limit,
        };

      case 'percentage':
        // Percentage aggregation doesn't use the query parameter
        // It builds its own queries with conditional filters
        return await this.executePercentageAggregation(config, scope);

      default:
        throw new Error(`Unknown aggregation type: ${aggregation.type}`);
    }
  }

  /**
   * SUB-5.12 · The names behind a group of user ids, in either scope.
   *
   * ⚠️ **`merged_scope_user_names()`, NOT `.from('users')`.** `users_select` is
   * `id = auth.uid() OR tenant_id = get_current_user_tenant_id() OR a user_roles
   * row in that same account`, so in merged scope a sub-account's own staff
   * cannot be read at all and the old `.eq('tenant_id', tenantId)` lookup here
   * returned nothing for them. Every one of their rows then rendered as
   * "Unknown User". `pipelinePaginationService.ts` and `lib/assigneeName.ts` are
   * the established callers of the RPC; this is the third.
   *
   * ⚠️ IT IS A NO-OP OUTSIDE MERGED SCOPE, which is why there is one path rather
   * than two. The function resolves through `merged_scope_tenant_ids()`, and in
   * single-account scope that set is the one account.
   *
   * ⚠️ IT STILL CANNOT NAME EVERYONE, and that is `open_work_by_member`'s
   * `renderCaveat` in `mergedMetrics.ts` rather than a defect here. A surface
   * grouping by person across accounts has to say what it does about the names.
   */
  private static async resolveUserNames(userIds: string[]): Promise<Record<string, string>> {
    if (userIds.length === 0) return {};

    const { data, error } = await supabase.rpc('merged_scope_user_names', {
      p_user_ids: userIds,
    });

    if (error || !Array.isArray(data)) {
      console.warn('[resolveUserNames] merged_scope_user_names failed:', error);
      return {};
    }

    return (data as AssigneeRecord[]).reduce((acc: Record<string, string>, user) => {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
      acc[user.user_id] = fullName || user.email || 'Unknown User';
      return acc;
    }, {});
  }

  /**
   * Check if groupBy field is a user reference that requires name resolution (DASH-7.1)
   */
  private static isUserGroupByField(field: string): boolean {
    return ['assigned_sales_rep_id', 'rep_id', 'assignee_id'].includes(field);
  }

  /**
   * Execute grouped count query
   * DASH-21.2: Optimized to use RPC functions instead of fetching all rows
   * Special handling for current_stage_id: joins to stages table for names and ordering
   */
  private static async executeGroupByCount(
    groupBy: string,
    config: DynamicWidgetConfig,
    checklistFilters: Map<string, string>,
    scope: WidgetReadScope,
    makeRowQuery: RowQueryFactory,
  ): Promise<any[]> {
    // DASH-21.2: Use optimized RPC function for fast database aggregation
    // Only fall back to old method if checklist filters are present (complex JOINs)
    //
    // SUB-5.12 · AC4. ⚠️ AND ONLY IN SINGLE-ACCOUNT SCOPE. `widget_count_grouped`
    // authorizes its own `p_tenant_id` against `get_current_user_tenant_id()`,
    // which in merged scope is the ANCHOR, so it would return the anchor's
    // counts with no error at all. `groupedCountUsesRpc` carries the reading and
    // the reasoning.
    if (checklistFilters.size === 0 && groupedCountUsesRpc(scope)) {
      return await this.executeGroupByCountRPC(groupBy, config.filters, scope);
    }

    // Fallback: the JavaScript path. Used for checklist JOINs, and for merged
    // scope, where the RPC cannot follow.
    //
    // SUB-5.12 · AC5 · F-101. ⚠️ THIS READ USED TO BE `await query` WITH NO
    // PAGING, so it aggregated at most 2,000 rows against a live total of 4,870
    // and rendered the result as a plain number. `readAllRows` owns the sort and
    // refuses to return a partial set.
    const { rows, error } = await readAllRowsAsync<any>(makeRowQuery as any, {
      orderBy: 'id',
    });
    if (error) throw error;
    const data = rows;

    // Special handling for stage grouping (fetch stage names/order separately)
    if (groupBy === 'current_stage_id') {
      // Get unique stage IDs and fetch stage metadata
      const stageIds = [...new Set((data || []).map((row: any) => row.current_stage_id).filter(Boolean))];
      let stageMap: Record<string, { name: string; order: number; pipeline_id: any }> = {};

      if (stageIds.length > 0) {
        const { data: stages, error: stageError } = await supabase
          .from('stages')
          .select('id, name, stage_order, pipeline_id')
          .in('id', stageIds);

        if (!stageError && stages) {
          stageMap = stages.reduce((acc: Record<string, { name: string; order: number; pipeline_id: any }>, stage: any) => {
            acc[stage.id] = { name: stage.name, order: stage.stage_order ?? 999, pipeline_id: stage.pipeline_id };
            return acc;
          }, {});
        }
      }

      // Group by stage with order preservation
      const grouped: Record<string, { name: string; count: number; order: number }> = {};
      (data || []).forEach((row: any) => {
        const stageId = row.current_stage_id || 'unknown';
        const stageInfo = stageMap[stageId];
        const stageName = stageInfo?.name || 'Unknown Stage';
        const stageOrder = stageInfo?.order ?? 999;
        const stagePipelineId = stageInfo?.pipeline_id;
        const projectPipelineId = row.pipeline_id;

        // Skip projects where current_stage_id points to a stage from a different pipeline
        if (stagePipelineId && projectPipelineId && stagePipelineId !== projectPipelineId) {
          return;
        }

        const key = String(stageId);
        if (!grouped[key]) {
          grouped[key] = { name: stageName, count: 0, order: stageOrder };
        }
        grouped[key].count++;
      });

      return Object.entries(grouped)
        .sort((a, b) => a[1].order - b[1].order)
        .map(([id, item]) => ({
          id,
          label: item.name,
          value: item.count,
        }));
    }

    // Special handling for user field grouping (fetch user names separately) - DASH-7.1
    if (this.isUserGroupByField(groupBy)) {
      const userIds = [...new Set((data || []).map((row: any) => row[groupBy]).filter(Boolean))];

      const userProfiles = await this.resolveUserNames(userIds as string[]);

      const grouped: Record<string, { name: string; count: number }> = {};
      (data || []).forEach((row: any) => {
        const userId = row[groupBy] || 'unknown';
        const userName = userProfiles[userId] || 'Unknown User';

        const key = String(userId);
        if (!grouped[key]) {
          grouped[key] = { name: userName, count: 0 };
        }
        grouped[key].count++;
      });

      return Object.entries(grouped).map(([id, data]) => ({
        id,
        name: data.name,
        value: data.count,
      }));
    }

    // Default grouping for other fields
    const grouped: Record<string, number> = {};
    (data || []).forEach((row: any) => {
      const key = row[groupBy] || 'Unknown';
      grouped[key] = (grouped[key] || 0) + 1;
    });

    return Object.entries(grouped).map(([key, count]) => {
      // CB-7.5 / UX-DR12: contracts grouped by envelope_status display the canonical human label
      // (never the raw lowercase-snake value) paired with the status accent color as the swatch.
      if (groupBy === 'envelope_status') {
        const meta = getEnvelopeStatusMeta(key);
        if (meta) {
          return { id: key, label: meta.label, value: count, color: meta.accent };
        }
      }
      return { id: key, label: key, value: count };
    });
  }

  /**
   * Execute numeric aggregation (sum/avg/min/max)
   */
  private static async executeNumericAggregation(
    type: 'sum' | 'avg' | 'min' | 'max',
    field: string,
    groupBy: string | undefined,
    makeRowQuery: RowQueryFactory,
  ): Promise<any> {
    // SUB-5.12 · AC5 · F-101. ⚠️ THIS WAS `const { data } = await query`, WITH NO
    // `.range()` AND NO `.limit()` ANYWHERE IN THE PATH. PostgREST caps a
    // collection response at 2,000 rows — measured 2026-08-17, `content-range:
    // 0-1999/4870` — so every sum, average, minimum and maximum was computed
    // over at most 2,000 of the matching rows and rendered as a perfectly
    // plausible number, with nothing saying so. This is F-97 in a second place.
    //
    // It is not live today: the only numeric-aggregation widget in the product
    // covers 760 projects. It becomes live on the first portfolio-wide figure,
    // which is what SUB-5.13 is.
    const { rows, error } = await readAllRowsAsync<any>(makeRowQuery as any, {
      orderBy: 'id',
    });
    if (error) throw error;
    const data = rows;

    if (groupBy) {
      // Grouped aggregation with user name resolution (DASH-7.1)
      if (this.isUserGroupByField(groupBy)) {
        // Extract unique user IDs
        const userIds = [...new Set((data || []).map((row: any) => row[groupBy]).filter(Boolean))];

        // Fetch user names for those IDs (DASH-7.1)
        const userProfiles = await this.resolveUserNames(userIds as string[]);

        const grouped: Record<string, { name: string; values: number[] }> = {};
        (data || []).forEach((row: any) => {
          const userId = row[groupBy] || 'unknown';
          const userName = userProfiles[userId] || 'Unknown User';
          const value = row[field];

          if (value !== null && value !== undefined) {
            const key = String(userId);
            if (!grouped[key]) {
              grouped[key] = { name: userName, values: [] };
            }
            grouped[key].values.push(Number(value));
          }
        });

        return Object.entries(grouped).map(([id, data]) => ({
          id,
          name: data.name,
          value: this.calculateAggregation(data.values, type),
        }));
      }

      // Grouped aggregation - calculate in JS from already-fetched data
      const grouped: Record<string, number[]> = {};
      (data || []).forEach((row: any) => {
        const key = row[groupBy] || 'Unknown';
        const value = row[field];
        if (value !== null && value !== undefined) {
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(Number(value));
        }
      });

      return Object.entries(grouped).map(([key, values]) => ({
        label: key,
        value: this.calculateAggregation(values, type),
      }));
    } else {
      // Simple aggregation
      const values = (data || []).map((row: any) => Number(row[field])).filter((v: number) => !isNaN(v));
      return this.calculateAggregation(values, type);
    }
  }

  /**
   * Calculate aggregation from array of numbers
   */
  private static calculateAggregation(values: number[], type: 'sum' | 'avg' | 'min' | 'max'): number {
    if (values.length === 0) return 0;

    switch (type) {
      case 'sum':
        return values.reduce((a, b) => a + b, 0);
      case 'avg':
        return values.reduce((a, b) => a + b, 0) / values.length;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
    }
  }

  /**
   * Execute percentage aggregation (DASH-6.1)
   * Calculates (numerator count / denominator count) * 100
   *
   * @param config - Widget configuration with percentage aggregation
   * @returns Percentage result with raw counts
   */
  private static async executePercentageAggregation(
    config: DynamicWidgetConfig,
    scope: WidgetReadScope,
  ): Promise<any> {
    try {
      // Extract percentage configuration
      const percentageConfig = config.aggregation.percentageConfig;
      if (!percentageConfig) {
        throw new Error('Percentage aggregation requires percentageConfig');
      }

      let numerator = 0;
      let denominator = 0;

      // MODE 1: Widget-based (recommended)
      //
      // ⚠️ SUB-5.12: this branch reads `dashboard_widgets`, whose SELECT policy
      // is `tenant_id = get_current_user_tenant_id()` — measured 2026-08-17, NOT
      // widened for merged scope. It is left alone deliberately: the source
      // widgets it names are rows on a tenant dashboard, and SUB-5.11 hides
      // those in merged scope, so this branch is unreachable there. A fixed
      // overview widget must not be built on it. ⚠️ This is also where F-99's
      // `completion_rate` would land, which is a second reason not to widen it
      // before that finding is answered.
      if (percentageConfig.numeratorWidgetId && percentageConfig.denominatorWidgetId) {
        // Fetch widget configurations
        const { data: widgets, error: widgetError } = await supabase
          .from('dashboard_widgets')
          .select('id, config')
          .in('id', [percentageConfig.numeratorWidgetId, percentageConfig.denominatorWidgetId]);

        if (widgetError) throw widgetError;
        if (!widgets || widgets.length !== 2) {
          throw new Error('Failed to fetch source widgets');
        }

        const numeratorWidget = widgets.find(w => w.id === percentageConfig.numeratorWidgetId);
        const denominatorWidget = widgets.find(w => w.id === percentageConfig.denominatorWidgetId);

        if (!numeratorWidget?.config || !denominatorWidget?.config) {
          throw new Error('Source widget configuration not found');
        }

        // BUGFIX: Inherit parent widget's date filter to source widgets
        // If parent widget has a dateRange filter (from global filters), apply it to source widgets
        // unless they already have their own dateRange filter
        const inheritDateFilter = (sourceConfig: DynamicWidgetConfig): DynamicWidgetConfig => {
          // Source widget already has its own date filter? Don't override
          if (sourceConfig.filters.dateRange) {
            return sourceConfig;
          }

          // Parent widget has a date filter (from global filters)? Apply it
          if (config.filters.dateRange) {
            return {
              ...sourceConfig,
              filters: {
                ...sourceConfig.filters,
                dateRange: config.filters.dateRange,
              },
            };
          }

          // No date filter to inherit
          return sourceConfig;
        };

        // Apply inherited filters to source widget configs
        const numeratorConfigWithFilters = inheritDateFilter(numeratorWidget.config);
        const denominatorConfigWithFilters = inheritDateFilter(denominatorWidget.config);

        // Execute numerator widget query with inherited filters
        const numeratorResult = await this.executeQuery(numeratorConfigWithFilters);
        numerator = typeof numeratorResult.data === 'number'
          ? numeratorResult.data
          : Array.isArray(numeratorResult.data)
            ? numeratorResult.data.length
            : 0;

        // Execute denominator widget query with inherited filters
        const denominatorResult = await this.executeQuery(denominatorConfigWithFilters);
        denominator = typeof denominatorResult.data === 'number'
          ? denominatorResult.data
          : Array.isArray(denominatorResult.data)
            ? denominatorResult.data.length
            : 0;
      }
      // MODE 2: Field-based (fallback/advanced)
      else {
        // Build base query with global filters applied (isSimpleCount = true for count-only queries).
        // Destructure { query } because applyFilters wraps its return — see its docstring.
        const buildQueryWithFilters = async () => {
          let query = this.buildBaseQuery(config.dataSource, scope, true);
          ({ query } = await this.applyFilters(query, config.filters, config.dataSource));
          return query;
        };

        // Execute numerator query
        let numeratorQuery: any = await buildQueryWithFilters();
        if (percentageConfig.numeratorCondition) {
          numeratorQuery = this.applyProjectFieldFilter(numeratorQuery, percentageConfig.numeratorCondition);
        }
        const { count: numeratorCount, error: numeratorError } = await numeratorQuery;
        if (numeratorError) throw numeratorError;
        numerator = numeratorCount ?? 0;

        // Execute denominator query
        let denominatorQuery: any = await buildQueryWithFilters();
        if (percentageConfig.denominatorCondition) {
          denominatorQuery = this.applyProjectFieldFilter(denominatorQuery, percentageConfig.denominatorCondition);
        }
        const { count: denominatorCount, error: denominatorError } = await denominatorQuery;
        if (denominatorError) throw denominatorError;
        denominator = denominatorCount ?? 0;
      }

      // Handle divide-by-zero
      if (denominator === 0 || denominator === null) {
        return {
          percentage: null,
          numerator,
          denominator,
          error: 'No data available'
        };
      }

      // Calculate percentage and round to whole number (no decimals)
      const percentage = Math.round((numerator / denominator) * 100);

      return {
        percentage,
        numerator,
        denominator
      };
    } catch (error) {
      console.error('[DynamicWidgetQueryService] Percentage aggregation failed:', error);
      return {
        percentage: null,
        numerator: 0,
        denominator: 0,
        error: 'Failed to calculate percentage'
      };
    }
  }

  /**
   * Calculate trend comparison vs previous period
   */
  private static async calculateTrend(config: DynamicWidgetConfig, currentData: any): Promise<any> {
    try {
      // Calculate current value
      const current = typeof currentData === 'number' ? currentData : currentData?.length || 0;

      // Build query for previous period
      const previousPeriodConfig = this.shiftDateRangeToPrevious(config);
      const previousData = await this.buildAndExecuteQuery(previousPeriodConfig);
      const previous = typeof previousData === 'number' ? previousData : previousData?.length || 0;

      // Calculate change
      const change = current - previous;
      const changePercent = previous > 0 ? (change / previous) * 100 : 0;
      const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

      return {
        current,
        previous,
        change,
        changePercent: Math.round(changePercent * 10) / 10,
        direction,
      };
    } catch (error) {
      console.warn('[DynamicWidgetQueryService] Trend calculation failed:', error);
      return null;
    }
  }

  /**
   * Shift date range to previous period for trend comparison
   */
  private static shiftDateRangeToPrevious(config: DynamicWidgetConfig): DynamicWidgetConfig {
    const newConfig = JSON.parse(JSON.stringify(config)); // Deep clone

    if (!newConfig.filters.dateRange) {
      // Default to last month if no date range specified
      newConfig.filters.dateRange = {
        type: 'this_month' as DateRangePreset,
        dateField: 'created_at',
      };
    }

    const dateRange = newConfig.filters.dateRange;
    const { start, end } = this.calculateDateRange(dateRange.type, dateRange.customStart, dateRange.customEnd);

    // Calculate duration
    const duration = end ? end.getTime() - start.getTime() : 30 * 24 * 60 * 60 * 1000;

    // Shift to previous period
    const previousStart = new Date(start.getTime() - duration);
    const previousEnd = new Date(start.getTime());

    newConfig.filters.dateRange = {
      type: 'custom' as DateRangePreset,
      dateField: dateRange.dateField || 'created_at',
      customStart: previousStart.toISOString(),
      customEnd: previousEnd.toISOString(),
    };

    return newConfig;
  }

  /**
   * Get filter options dynamically from tenant data
   */
  static async getFilterOptions(filterType: string, params?: any): Promise<any[]> {
    try {
      // ⚠️ SUB-5.12: there used to be a `getCurrentTenantId()` call here whose
      // result was null-checked and then never referenced. Every branch below
      // relies on RLS, and each says so. It was counted as one of this class's
      // seven tenant pins and was not one; removing it changes no query.
      //
      // ⚠️ THE OPTION LISTS ARE STILL THE ANCHOR'S IN MERGED SCOPE, and that is
      // a policy fact rather than an omission here: `pipelines_select`,
      // `stages_select`, `teams_select` and `users_select` are all
      // `tenant_id = get_current_user_tenant_id()`. It is exactly why
      // `mergedFilters.ts` withholds Pipeline, Stage, Checklist item and Team
      // across accounts instead of offering a picker that lists one account's
      // rows and matches none of the others'.
      switch (filterType) {
        case 'pipelines':
          // RLS automatically filters by tenant, no need for manual .eq('tenant_id', tenantId)
          const { data: pipelines, error: pipelineError } = await supabase
            .from('pipelines')
            .select('id, name, pipeline_type')
            .order('name');

          if (pipelineError) {
            console.error('[getFilterOptions] Pipeline query error:', pipelineError);
            throw pipelineError;
          }
          return pipelines || [];

        case 'stages':
          // RLS automatically filters by tenant
          let stageQuery = supabase
            .from('stages')
            .select('id, name, pipeline_id')
            .order('stage_order');

          // Optionally filter by pipeline
          if (params?.pipelineIds && params.pipelineIds.length > 0) {
            stageQuery = stageQuery.in('pipeline_id', params.pipelineIds);
          }

          const { data: stages, error: stagesError } = await stageQuery;
          if (stagesError) {
            console.error('[getFilterOptions] Stages query error:', stagesError);
            throw stagesError;
          }
          return stages || [];

        case 'users':
          // RLS automatically filters by tenant
          const { data: users, error: usersError } = await supabase
            .from('users')
            .select('id, first_name, last_name, email')
            .order('first_name');
          if (usersError) {
            console.error('[getFilterOptions] Users query error:', usersError);
            throw usersError;
          }
          return users || [];

        case 'teams':
          // RLS automatically filters by tenant
          const { data: teams, error: teamsError } = await supabase
            .from('teams')
            .select('id, name')
            .order('name');
          if (teamsError) {
            console.error('[getFilterOptions] Teams query error:', teamsError);
            throw teamsError;
          }
          return teams || [];

        case 'statuses':
          // RLS automatically filters by tenant
          const { data: statusData, error: statusError } = await supabase
            .from('projects')
            .select('project_status')
            .not('project_status', 'is', null);
          if (statusError) {
            console.error('[getFilterOptions] Statuses query error:', statusError);
            throw statusError;
          }
          const statuses = [...new Set((statusData || []).map(d => d.project_status))];
          return statuses;

        case 'dispositions':
          // RLS automatically filters by tenant
          const { data: dispData, error: dispError } = await supabase
            .from('appointments')
            .select('disposition')
            .not('disposition', 'is', null);
          if (dispError) {
            console.error('[getFilterOptions] Dispositions query error:', dispError);
            throw dispError;
          }
          const dispositions = [...new Set((dispData || []).map(d => d.disposition))];
          return dispositions;

        case 'states':
          // RLS automatically filters by tenant
          const { data: stateData, error: stateError } = await supabase
            .from('projects')
            .select('project_state')
            .not('project_state', 'is', null);
          if (stateError) {
            console.error('[getFilterOptions] States query error:', stateError);
            throw stateError;
          }
          const states = [...new Set((stateData || []).map(d => d.project_state))];
          return states.map(s => ({ value: s, label: s }));

        case 'cities':
          // RLS automatically filters by tenant
          const { data: cityData, error: cityError } = await supabase
            .from('projects')
            .select('project_city')
            .not('project_city', 'is', null);
          if (cityError) {
            console.error('[getFilterOptions] Cities query error:', cityError);
            throw cityError;
          }
          const cities = [...new Set((cityData || []).map(d => d.project_city))];
          return cities;

        default:
          console.warn(`Unknown filter type: ${filterType}`);
          return [];
      }
    } catch (error) {
      console.error(`[DynamicWidgetQueryService] Failed to load ${filterType} options:`, error);
      return [];
    }
  }

  /**
   * Execute grouped count using optimized RPC function (DASH-21.2)
   * Performs aggregation in database instead of JavaScript
   * Special handling for user fields to fetch names (for leaderboards)
   * @returns Array of {label, value} for charts OR {id, name, value} for leaderboards
   */
  private static async executeGroupByCountRPC(
    groupBy: string,
    filters: DynamicWidgetConfig['filters'],
    scope: WidgetReadScope,
  ): Promise<any[]> {
    try {
      // SUB-5.12 · AC4. ⚠️ THE PRECONDITION IS ASSERTED HERE, NOT ONLY AT THE
      // CALL SITE. `widget_count_grouped` takes a tenant id and authorizes it
      // against `get_current_user_tenant_id()`, so calling it in merged scope
      // with the anchor returns the anchor's counts and no error. A future
      // caller that forgets gets a refusal instead of a wrong number.
      if (scope.kind !== 'single') {
        throw new WidgetScopeRefusal(
          'This grouped count can only be computed one account at a time.',
        );
      }
      const tenantId = scope.tenantId;

      // Build filters object for RPC function
      const rpcFilters: any = {};
      if (filters.pipelineIds && filters.pipelineIds.length > 0) {
        rpcFilters.pipelineIds = filters.pipelineIds;
      }
      if (filters.stageIds && filters.stageIds.length > 0) {
        rpcFilters.includedStageIds = filters.stageIds;
      }
      // DASH-24: Add dateRange filter for leaderboard accuracy
      if (filters.dateRange) {
        const { start, end } = this.calculateDateRange(
          filters.dateRange.type,
          filters.dateRange.customStart,
          filters.dateRange.customEnd
        );
        if (start || end) {
          rpcFilters.dateRange = {
            dateField: filters.dateRange.dateField || 'created_at',
            // Convert Date objects to ISO strings for PostgreSQL
            start: start ? start.toISOString() : null,
            end: end ? end.toISOString() : null,
          };
        }
      }

      // Call RPC function with widget filters
      const { data, error } = await supabase.rpc('widget_count_grouped', {
        p_table_name: 'projects',
        p_group_by_column: groupBy,
        p_tenant_id: tenantId,
        p_filters: rpcFilters,
      });

      if (error) {
        console.error('[executeGroupByCountRPC] RPC error:', error);
        throw error;
      }

      // Special handling for user field grouping (leaderboards need id + name)
      if (this.isUserGroupByField(groupBy)) {
        // RPC function already returns group_id (UUID) and group_name (full name)
        // No need to fetch user names again - they're already included!
        return (data || []).map((row: any) => ({
          id: row.group_id || row.group_name,  // group_id contains the user UUID
          name: row.group_name || 'Unknown User',  // group_name already contains full name
          value: Number(row.count),
        }));
      }

      // Standard chart format: {id, label, value}
      // DASH-24.2: Include id for click-through filter derivation
      return (data || []).map((row: any) => ({
        id: row.group_id || row.group_name,  // group_id contains the actual ID
        label: row.group_name,
        value: Number(row.count),
      }));
    } catch (error) {
      console.error('[executeGroupByCountRPC] Failed:', error);
      throw error;
    }
  }

  /**
   * SUB-5.14 · D90. List an explicit, ordered set of projects.
   *
   * ⚠️ **PAGED BY SLICING THE ID ARRAY, NOT WITH `.range()`.** Two reasons, and
   * both are correctness rather than taste:
   *
   *   1. The order is the FIGURE's ranking — worst overrun first, longest
   *      untouched first — and no column reproduces it, so a server-side
   *      `.order()` would re-sort the answer into a different one.
   *   2. `total` is `ids.length` exactly, with no `count: 'exact'` round trip
   *      and no chance of the count and the rows disagreeing.
   *
   * ⚠️ One page of ids is 25 by default, far under the 640-id ceiling measured
   * in `projectListFilters.ts` on 2026-08-16, so this needs no chunking. The
   * ceiling still matters and is why the ids are sliced rather than sent whole.
   *
   * ⚠️ **RLS STILL BOUNDS THIS.** `projects_select` re-authorizes every id, so a
   * caller cannot widen anything by naming a row. A row the policy refuses
   * simply does not come back, which is why the result is filtered rather than
   * assumed to be complete.
   */
  private static async fetchProjectsByIds(
    ids: readonly string[],
    scope: WidgetReadScope,
    options: { offset: number; limit: number },
  ): Promise<ProjectListResult> {
    const { offset, limit } = options;
    const total = ids.length;
    const pageIds = ids.slice(offset, offset + limit);

    if (pageIds.length === 0) {
      return { items: [], total, hasMore: false, offset, limit };
    }

    const { data, error } = await this.applyScope(
      supabase
        .from('projects')
        .select(DRAWER_COLUMNS)
        .is('deleted_at', null) as unknown as PostgrestFilterBuilder<any, any, any>,
      scope,
    ).in('id', pageIds);

    if (error) throw error;

    const byId = new Map<string, any>((data ?? []).map((row: any) => [row.id, row]));
    // ⚠️ REORDERED TO THE CALLER'S SEQUENCE. `.in()` returns rows in whatever
    // order the planner produces, which would silently discard the ranking.
    const ordered = pageIds.map((id) => byId.get(id)).filter(Boolean);

    const items = await this.toDrawerItems(ordered);
    return { items, total, hasMore: offset + limit < total, offset, limit };
  }

  /**
   * SUB-5.14. Shape rows for the panel, resolving the names that a PostgREST
   * embed cannot reach across accounts.
   *
   * ⚠️ **THE EMBEDS COME BACK NULL FOR A SUB-ACCOUNT ROW, AND NOT AS AN ERROR.**
   * An embed is bounded by the NARROWER of the two policies. `projects_select`
   * resolves merged scope; `stages_select`, `pipelines_select` and
   * `users_select` are all `tenant_id = get_current_user_tenant_id()`. So the
   * project is returned and its stage, pipeline and rep are not. This is F-50's
   * and SUB-4.16's mechanism on a third surface, and both previous times it was
   * found by someone using the feature rather than by a test.
   *
   * Stage and rep are resolved through the merged RPCs, which are no-ops outside
   * merged scope. ⚠️ **PIPELINE HAS NO SUCH RPC and is not resolved**: Allan
   * chose to show the ACCOUNT in its place on a cross-account list, which is the
   * more useful column there and needs no migration. A pipeline name means a
   * different thing in each account anyway, which is `mergedFilters.ts`'s own
   * reason for withholding the pipeline filter across accounts.
   */
  private static async toDrawerItems(rows: readonly any[]): Promise<ProjectListItemData[]> {
    const stageIds = [...new Set(rows.map((r) => r.current_stage_id).filter(Boolean))];
    const repIds = [...new Set(rows.map((r) => r.assigned_sales_rep_id).filter(Boolean))];

    const [stageNames, repNames] = await Promise.all([
      this.resolveStageNames(stageIds as number[]),
      this.resolveUserNames(repIds as string[]),
    ]);

    return rows.map((row: any) => ({
      id: row.id,
      tenant_id: row.tenant_id ?? null,
      project_name: row.project_name,
      company_name: row.company_name,
      project_status: row.project_status,
      current_stage_id: row.current_stage_id,
      // The embed first, so single-account scope is byte for byte what it was,
      // then the merged resolution for the rows the embed could not reach.
      stage_name: row.stages?.name ?? stageNames?.[String(row.current_stage_id)]?.stage_name ?? null,
      stage_cell: row.stages?.name
        ? ({ kind: 'named', name: row.stages.name } as StageCell)
        : stageNameCell(stageNames?.[String(row.current_stage_id)], stageNames === null),
      pipeline_id: row.pipeline_id,
      pipeline_name: row.pipelines?.name || null,
      assigned_sales_rep_id: row.assigned_sales_rep_id,
      assigned_rep_name: row.assigned_rep
        ? [row.assigned_rep.first_name, row.assigned_rep.last_name].filter(Boolean).join(' ') || null
        : repNames[row.assigned_sales_rep_id] || null,
      created_at: row.created_at,
      updated_at: row.updated_at,
      stage_changed_at: row.stage_changed_at,
      system_cost: row.system_cost,
      estimated_system_size: row.estimated_system_size,
      project_city: row.project_city,
      project_state: row.project_state,
    }));
  }

  /**
   * SUB-5.14. Stage names a merged caller may read, via SUB-4.15's function.
   * A no-op outside merged scope, where the embed already answered.
   */
  private static async resolveStageNames(
    stageIds: number[],
  ): Promise<Record<string, StageNameRow> | null> {
    if (stageIds.length === 0) return {};

    const { data, error } = await supabase.rpc('merged_scope_stage_names', {
      p_stage_ids: stageIds,
    });

    // ⚠️ NULL, NOT `{}`. A failed call makes every stage on this page UNKNOWN;
    // an empty map would make them all "no stage", which is a different and
    // untrue answer. `stageNameCell`'s `readFailed` argument exists for exactly
    // this distinction.
    if (error || !Array.isArray(data)) {
      console.warn('[resolveStageNames] merged_scope_stage_names failed:', error);
      return null;
    }

    return (data as StageNameRow[]).reduce((acc: Record<string, StageNameRow>, row) => {
      acc[String(row.stage_id)] = row;
      return acc;
    }, {});
  }

  /**
   * Fetch projects for the Project List Drawer (DASH-24.1)
   * Applies widget filters + optional segment filter for click-through
   *
   * @param filters - Widget filters to apply
   * @param options - Pagination and segment options
   * @returns Paginated project list with related data
   */
  static async fetchProjectsForDrawer(
    filters: DerivedFilters,
    options: {
      offset?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    } = {}
  ): Promise<ProjectListResult> {
    const { offset = 0, limit = 25, sortBy = 'updated_at', sortOrder = 'desc' } = options;

    try {
      console.log('[fetchProjectsForDrawer] Full filters received:', JSON.stringify(filters, null, 2));

      // SUB-5.12 · AC2. ⚠️ THIS WAS `.eq('tenant_id', await getCurrentTenantId())`,
      // which is the ANCHOR in merged scope, and it is the pin D58 chose
      // `/app/projects` over rather than remove. Removing it is safe now and it
      // was measured rather than assumed: this method has exactly ONE caller
      // (`useProjectListQuery.ts:114`), used by ONE component
      // (`ProjectListDrawer.tsx`), rendered by ONE page (`DashboardView.tsx`),
      // and SUB-5.11 hides tenant dashboards in merged scope, so the drawer is
      // unreachable there today. `PortfolioRollup` matches a grep for
      // `ProjectListDrawer` in a COMMENT only. SUB-5.14 records that reversal as
      // D87.
      const scope = await this.resolveReadScope('projects');
      if (scope.kind === 'refused') {
        throw new WidgetScopeRefusal(scope.reason);
      }

      /**
       * SUB-5.14 · D90. A portfolio figure hands over the exact set it counted.
       *
       * ⚠️ **IT SHORT-CIRCUITS EVERY OTHER FILTER, ON PURPOSE.** The ids ARE
       * the answer. Intersecting them with a predicate could only ever show
       * FEWER rows than the figure counted, which is precisely the
       * figure-disagrees-with-its-list defect this design removes.
       */
      if (filters.projectIds) {
        return await this.fetchProjectsByIds(filters.projectIds, scope, {
          offset,
          limit,
        });
      }

      // Mirror buildAndExecuteQuery: separate checklist filters so we can add the
      // INNER JOIN to the select clause before applyFilters references it.
      const notExistsFilters: ProjectFieldFilter[] = [];
      const joinableChecklistFilters: ProjectFieldFilter[] = [];

      if (filters.projectFields) {
        filters.projectFields.forEach((filter) => {
          if (filter.source === 'checklist_item' && filter.checklistItemId) {
            if (filter.operator === 'not_exists') {
              notExistsFilters.push(filter);
            } else {
              joinableChecklistFilters.push(filter);
            }
          }
        });
      }

      // Build select clause with drawer columns + relations + checklist JOINs
      const drawerSelectColumns = DRAWER_COLUMNS;
      const checklistJoinParts = joinableChecklistFilters.map((filter) => {
        const alias = `pci_${filter.checklistItemId}`;
        return `${alias}:project_checklist_items!inner(*)`;
      });
      const fullSelect = checklistJoinParts.length > 0
        ? `${drawerSelectColumns}, ${checklistJoinParts.join(', ')}`
        : drawerSelectColumns;

      let query: PostgrestFilterBuilder<any, any, any> = this.applyScope(
        supabase
          .from('projects')
          .select(fullSelect, { count: 'exact' })
          .is('deleted_at', null) as unknown as PostgrestFilterBuilder<any, any, any>,
        scope,
      );

      // Handle not_exists via subquery exclusion (same approach as buildAndExecuteQuery)
      for (const filter of notExistsFilters) {
        const itemId = typeof filter.checklistItemId === 'string'
          ? parseInt(filter.checklistItemId, 10)
          : filter.checklistItemId;
        if (!itemId || isNaN(itemId as number)) continue;

        let subQuery = this.applyScope(
          supabase
            .from('projects')
            .select('id, pci:project_checklist_items!inner(template_item_id)')
            .is('deleted_at', null) as unknown as PostgrestFilterBuilder<any, any, any>,
          scope,
        ).eq('pci.template_item_id', itemId);

        if (filter.dataType === 'date') {
          subQuery = subQuery.not('pci.value_date', 'is', null);
        } else if (filter.dataType === 'boolean') {
          subQuery = subQuery.eq('pci.is_completed', true);
        } else {
          subQuery = subQuery.not('pci.value', 'is', null);
        }

        const { data: projectsWithValue } = await subQuery.limit(10000);
        if (projectsWithValue && projectsWithValue.length > 0) {
          const excludeIds = projectsWithValue.map((p: any) => p.id);
          query = query.not('id', 'in', `(${excludeIds.join(',')})`);
        }
      }

      // Apply ALL filters via the canonical applyFilters method.
      // This ensures the drawer query stays in sync with the widget count query —
      // pipelineIds, stageIds, dateRange, statuses, location, ranges,
      // lastActivityThreshold, projectFields, hasPendingContracts, etc.
      // Destructure { query } because applyFilters wraps its return — see its docstring.
      ({ query } = await this.applyFilters(
        query,
        filters as DynamicWidgetConfig['filters'],
        'projects'
      ));

      // Apply segment filter if present (drawer-only, comes from chart click)
      if (filters.segmentFilter) {
        const { field, value } = filters.segmentFilter;
        console.log('[fetchProjectsForDrawer] Applying segment filter:', { field, value });
        query = query.eq(field, value);
      }

      // Apply sorting and pagination
      query = query
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error('[fetchProjectsForDrawer] Query error:', error);
        throw error;
      }

      // SUB-5.14. One shaping path for both reads, so the merged name
      // resolution cannot be present on one and missing on the other.
      const items: ProjectListItemData[] = await this.toDrawerItems(data || []);

      const total = count ?? 0;

      return {
        items,
        total,
        hasMore: offset + limit < total,
        offset,
        limit,
      };
    } catch (error: any) {
      console.error('[fetchProjectsForDrawer] Failed:', error);
      throw new Error(`Failed to fetch projects: ${error?.message || 'Unknown error'}`);
    }
  }
}
